import useStateValue from "frontend/state/value";
import style from "./app-header.module.css";
import InviteButtonsTopBar from "frontend/modals/invite-buttons-topbar";
import { useBoardValue } from "frontend/state/board-state";
import { InstanceType } from "shared/datamodel/schemas/ability";
import useAbility from "frontend/hooks/use-ability";
import usePlanFeatureValue from "frontend/hooks/use-plan-features";
import { CheckoutSource } from "frontend/billing/checkout";
import OfflineIcon from "./offline-icon";
import { useAtomValue } from "jotai";
import { isUserDisconnectedAtom } from "state-atoms";
import SaveAsTemplate from "./save-as-template";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import CursorVisibilityDropdown from "./show-cursors-dropdown";
import { isCIM } from "utils/url-utils";
import SignUpButton from "frontend/ui-components/sign-up-button/sign-up-button";
import { VideoCallOngoing } from "frontend/canvas-controls/video-call-ongoing/video-call-ongoing";
import { PlanFeatureName } from "shared/datamodel/schemas";
import { isBasicPlan } from "shared/util/billing";

export default function AppHeaderBoard({
  title = null,
  renderUpgradeCTA,
  renderBoardUsersPresence,
  children,
  onInviteClick,
  onCreateTemplate,
  onCreateTemplateLink,
  setShowUpgradeModal,
  canEditBoard,
}: {
  title: any;
  renderUpgradeCTA?: () => JSX.Element;
  renderBoardUsersPresence?: () => JSX.Element;
  children?: any;
  onInviteClick?: (title?: string) => void;
  onCreateTemplate: () => void;
  onCreateTemplateLink: () => void;
  setShowUpgradeModal: (reason: CheckoutSource | null) => void;
  canEditBoard?: boolean | null;
}) {
  const [{ user }] = useStateValue();
  const [{ user: userValue, board }] = useBoardValue();
  const isUserDisconnected = useAtomValue(isUserDisconnectedAtom);
  const allowCursorVisibilityControl = useFeatureFlag("cursor-visibility-control");

  const { canPerformAnyAction, canShareBoard } = useAbility();

  const shouldShowSignUpButton = userValue && userValue.isAnonymous && !user;

  const shouldShowShareButton =
    user && board && (canShareBoard(user, board) || !canPerformAnyAction(user, board, InstanceType.Board));

  const shouldRenderUpgradeCTA =
    userValue &&
    !userValue.isAnonymous &&
    isBasicPlan(user?.planInfo?.plan_id) &&
    user?.planInfo?.source !== "touch" &&
    renderUpgradeCTA;

  const createTemplateEnabled = !!usePlanFeatureValue(PlanFeatureName.CREATE_TEMPLATE_ENABLED);

  const renderShareButton = () => {
    if (shouldShowShareButton && !userValue?.isAnonymous) {
      return <InviteButtonsTopBar onInviteClick={onInviteClick} />;
    }
    return null;
  };

  function onSaveTemplateToLibrary() {
    createTemplateEnabled ? onCreateTemplate() : setShowUpgradeModal({ name: "create-template", value: "save-to-lib" });
  }

  function onSaveTemplateAsLink() {
    createTemplateEnabled
      ? onCreateTemplateLink()
      : setShowUpgradeModal({ name: "create-template", value: "save-to-link" });
  }

  const renderSaveAsTemplateButton = () => {
    let shouldRender = board && board.hasMemberAccess && canEditBoard;
    if (isCIM()) {
      shouldRender = Boolean(!user?.isGuest && shouldRender);
    }
    if (shouldRender && board) {
      return (
        <>
          <div className={style.verticalDivider} />
          <SaveAsTemplate
            onSaveTemplateToLibrary={onSaveTemplateToLibrary}
            onSaveTemplateAsLink={onSaveTemplateAsLink}
            createTemplateEnable={createTemplateEnabled}
            boardDocumentId={board.documentId}
          />
        </>
      );
    } else {
      return null;
    }
  };

  return (
    <>
      {renderSaveAsTemplateButton()}
      {title && <div>{title}</div>}
      <div className={style.right}>
        <div className={style.content}>{children}</div>
        {canEditBoard && allowCursorVisibilityControl && <CursorVisibilityDropdown />}
        {isUserDisconnected && <OfflineIcon />}
        {renderBoardUsersPresence && renderBoardUsersPresence()}
        <div className={style.contentButtons}>
          {renderShareButton()}
          {shouldRenderUpgradeCTA && renderUpgradeCTA()}
          {shouldShowSignUpButton && <SignUpButton />}
        </div>
        <VideoCallOngoing />
      </div>
    </>
  );
}
